import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { supabase } from '../integrations/supabase/client';
import { CheckCircle2, X, Loader2, Twitter } from 'lucide-react';

function randomQuestion() {
  const a  = Math.floor(Math.random() * 12) + 3;
  const b  = Math.floor(Math.random() * 9)  + 1;
  const op = Math.random() > 0.5 ? '+' : '-';
  const answer = op === '+' ? a + b : a - b;
  return { text: `${a} ${op} ${b}`, answer };
}

export function WaitlistDialog({ open, onClose }) {
  const [wallet,  setWallet]  = useState('');
  const [answer,  setAnswer]  = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Regenerate math question every time the dialog opens
  const [question, setQuestion] = useState(randomQuestion);

  useEffect(() => {
    if (open) { setWallet(''); setAnswer(''); setSuccess(false); setQuestion(randomQuestion()); }
  }, [open]);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!/^0x[a-fA-F0-9]{40}$/.test(wallet.trim())) {
      toast.error('Please enter a valid wallet address (0x...)');
      return;
    }
    if (parseInt(answer, 10) !== question.answer) {
      toast.error("Wrong answer — try again to prove you're human!");
      return;
    }

    setLoading(true);
    const { error } = await supabase
      .from('waitlist_submissions')
      .insert({ wallet_address: wallet.trim().toLowerCase() });
    setLoading(false);

    if (error) {
      if (error.code === '23505') {
        toast.error('This wallet is already on the list!');
      } else {
        toast.error(error.message);
      }
      return;
    }
    setSuccess(true);
  }

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    "Just locked my WL spot 🔒 @Glummies_\n\nSecure yours before it's gone 👉 https://glummies.xyz/"
  )}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ backgroundColor: 'rgba(30,30,45,0.6)' }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.85, y: 40, opacity: 0 }}
            animate={{ scale: 1,    y: 0,  opacity: 1 }}
            exit={{   scale: 0.9,   y: 20, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="relative w-full max-w-md rounded-3xl border-2 border-ink bg-card p-8 shadow-pop"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-1 hover:bg-muted"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <AnimatePresence mode="wait">
              {!success ? (
                /* ── Form ───────────────────────────────────── */
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -20 }}
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div>
                    <h3 className="text-2xl font-bold">
                      Secure your <span className="rainbow-text">spot</span>
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Drop your wallet and prove you're human.
                    </p>
                  </div>

                  {/* Wallet field */}
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider">
                      Wallet address
                    </label>
                    <input
                      value={wallet}
                      onChange={(e) => setWallet(e.target.value)}
                      placeholder="0x..."
                      className="w-full rounded-xl border-2 border-ink bg-background px-4 py-3 font-mono text-sm outline-none transition focus:ring-4 focus:ring-primary/40"
                      required
                    />
                  </div>

                  {/* Anti-bot math */}
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wider">
                      Anti-bot check
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl border-2 border-ink bg-muted px-4 py-3 font-mono font-bold">
                        {question.text} = ?
                      </div>
                      <input
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        type="number"
                        placeholder="?"
                        className="w-24 rounded-xl border-2 border-ink bg-background px-4 py-3 text-center font-mono outline-none focus:ring-4 focus:ring-primary/40"
                        required
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    disabled={loading}
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-ink bg-primary py-3 font-bold text-primary-foreground shadow-pop disabled:opacity-70"
                  >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? 'Submitting…' : 'Submit & Secure Spot'}
                  </motion.button>
                </motion.form>
              ) : (
                /* ── Success ─────────────────────────────────── */
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-5 text-center"
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary"
                  >
                    <CheckCircle2 className="h-12 w-12 text-primary-foreground" />
                  </motion.div>

                  <div>
                    <h3 className="text-2xl font-bold">You're in! 🎉</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Your wallet is saved. Now shout it on X to lock it in.
                    </p>
                  </div>

                  <a
                    href={tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-ink bg-foreground py-3 font-bold text-card shadow-pop"
                  >
                    <Twitter className="h-4 w-4" /> Post on X
                  </a>

                  <button
                    onClick={onClose}
                    className="w-full text-sm text-muted-foreground hover:underline"
                  >
                    Close
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}